import { datadogRum } from '@datadog/browser-rum-slim';
import {
  allClientSideFlags,
  clientSideId,
  datadogEnvironment,
  dogfoodBaseUri,
  dogfoodClientSideEventsUri,
  dogfoodContext,
  dogFoodEventsCapacity,
  dogfoodSendEvents,
  dogfoodStreamUri,
  enableFullstoryFlagVarsIntegration,
  frontendDatadogFlagRumTracking,
  frontendVersion,
  observabilityOTELUrl,
  observabilityProjectID,
  observabilityPublicGraphUrl,
  secureModeContextHash,
} from '@gonfalon/constants/constants';
import type { GonfalonWindow } from '@gonfalon/types/GonfalonWindow';
import type {
  LDContext,
  LDContextCommon,
  LDEvaluationDetail,
  LDInspection,
  LDMultiKindContext,
  LDPlugin,
} from '@launchdarkly/js-client-sdk';
import { createClient } from '@launchdarkly/js-client-sdk';
import Observability from '@launchdarkly/observability';
import SessionReplay from '@launchdarkly/session-replay';

import { eventInterceptionPlugin, flagOverridePlugin } from './plugins';
import { recordFlagToMeticulous } from './recordFlagToMeticulous';

declare global {
  interface Window extends GonfalonWindow {}
}

const evaluatedSessionFlags: { [path: string]: { [flagKey: string]: unknown } } = {};

function isMultiContext(context: LDContext): context is LDMultiKindContext {
  return 'kind' in context && context.kind === 'multi';
}

/**
 * Get a friendly name for the context. When this function returns undefined
 * then the default context name handling will be used (which is the canonical key).
 *
 * So we make a best effort to get a user friendly name, and if for some reason
 * the context doesn't match our expectation, we still get a usable name.
 * @param context The context to get a friendly name for.
 * @returns Either a string containing the friendly name, or undefined to use the default.
 */
function contextFriendlyName(context: LDContext): string | undefined {
  if (isMultiContext(context)) {
    if (context.member) {
      const member = context.member as LDContextCommon;
      if (member.email) {
        return member.email;
      } else {
        return member.key;
      }
    }
  }

  return undefined;
}

export function createDogfoodClient() {
  const inspectors: LDInspection[] = [];
  if (frontendDatadogFlagRumTracking()) {
    const ddInspector: LDInspection = {
      type: 'flag-used',
      name: 'dd-inspector',
      method: (key: string, detail: LDEvaluationDetail) => {
        datadogRum.addFeatureFlagEvaluation(key, detail.value);
      },
    };

    inspectors.push(ddInspector);
  }

  if (enableFullstoryFlagVarsIntegration()) {
    const fsInspector: LDInspection = {
      type: 'flag-used',
      name: 'fs-inspector',
      method: (key: string, detail: LDEvaluationDetail) => {
        // getCurrentSessionUrl exists, I swear :P
        // It's attached by fullstory.
        if (window.FS === undefined || typeof window.FS.getCurrentSessionURL !== 'function') {
          return;
        }

        const url = window.FS.getCurrentSessionURL(true);
        if (url === undefined) {
          return;
        }

        evaluatedSessionFlags[url] = {
          ...evaluatedSessionFlags[url],
          [key]: detail.value,
        };

        const arrayOfFlags = Object.keys(evaluatedSessionFlags[url]).map(
          (flagKey) => `${flagKey}=${JSON.stringify(evaluatedSessionFlags[url][flagKey], null, 1)}`,
        );

        // setVars exists, I swear :P
        // It's attached by fullstory.
        // https://help.fullstory.com/hc/en-us/articles/1500004101581-FS-setVars-API-Sending-custom-page-data-to-FullStory
        if (typeof window.FS.setVars === 'function') {
          window.FS.setVars('page', { ldflags: arrayOfFlags });
        }
      },
    };

    inspectors.push(fsInspector);
  }

  // Enabling Meticulous is already guarded by a flag; if Meticulous is enabled we'll record feature flags there
  if (window.Meticulous) {
    const meticulousInspector: LDInspection = {
      type: 'flag-used',
      name: 'meticulous-inspector',
      method: (key: string, detail: LDEvaluationDetail) => {
        recordFlagToMeticulous(key, detail.value);
      },
    };
    inspectors.push(meticulousInspector);
  }

  const projectID = observabilityProjectID();
  // In v0.3.0+, projectID is no longer passed as first argument to plugin constructors,
  // but a truthy projectID value still indicates that observability plugins should be enabled

  // @ts-expect-error - TODO: Will depend on the updates to new toolbar.
  const plugins: LDPlugin[] = projectID
    ? [
        new Observability({
          networkRecording: {
            enabled: true,
            recordHeadersAndBody: true,
          },
          reportConsoleErrors: true,
          tracingOrigins: [
            'launchdarkly.com/api',
            'launchdarkly.com/internal',
            'ld.catamorphic.com/api',
            'ld.catamorphic.com/internal',
            'localhost/api',
            'localhost/internal',
            'pri.observability.app.ld.catamorphic.com',
            'pri.observability.app.launchdarkly.com',
            'pri.observability.ld-stg.launchdarkly.com',
            'product-analytics-api.ld-stg.launchdarkly.com',
            'product-analytics-api.app.ld.catamorphic.com',
            'product-analytics-api.app.launchdarkly.com',
          ],
          serviceName: 'gonfalon-web',
          environment: datadogEnvironment(),
          version: process.env.NODE_ENV === 'production' ? frontendVersion() : undefined,
          backendUrl: observabilityPublicGraphUrl(),
          otel: {
            otlpEndpoint: observabilityOTELUrl(),
          },
          contextFriendlyName,
          flagExposureDedupeWindowMillis: 5 * 60 * 1000,
        }),
        new SessionReplay({
          privacySetting: 'none',
          serviceName: 'gonfalon-web',
          environment: datadogEnvironment(),
          version: process.env.NODE_ENV === 'production' ? frontendVersion() : undefined,
          backendUrl: observabilityPublicGraphUrl(),
          contextFriendlyName,
          enableCanvasRecording: true,
          samplingStrategy: {
            canvasManualSnapshot: 1,
            canvasMaxSnapshotDimension: 720,
          },
          maskTextClass: 'fs-exclude',
        }),
        flagOverridePlugin,
        eventInterceptionPlugin,
      ]
    : [flagOverridePlugin, eventInterceptionPlugin];

  const client = createClient(clientSideId(), dogfoodContext(), {
    applicationInfo: {
      id: 'gonfalon-frontend',
      version: process.env.NODE_ENV === 'production' ? frontendVersion() : undefined,
    },
    baseUri: dogfoodBaseUri(),
    streamUri: dogfoodStreamUri(),
    eventsUri: dogfoodClientSideEventsUri(),
    streaming: false,
    sendEvents: dogfoodSendEvents(),
    inspectors,
    plugins,
    capacity: dogFoodEventsCapacity() || 100,
    maxCachedContexts: 0,
  });

  void client.start({
    bootstrap: allClientSideFlags(),
    identifyOptions: {
      hash: secureModeContextHash(),
    },
  });

  return client;
}
